export const formatCurrency = (
  value: number | undefined,
  _locale: string = "en",
  minimumFractionDigits: number = 2,
): string => {
  const formattedValue = new Intl.NumberFormat(
    "en-CA",
    // locale === "fr" ? "fr-CA" : "en-CA",
    {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: minimumFractionDigits,
      maximumFractionDigits: 2,
    },
  ).format(value ?? 0);

  return formattedValue.replace(/,/g, " ");
};

/** A day with no time part: "2026-11-01". Never shifted between zones. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A calendar day serialised as a timestamp at UTC midnight —
 * "2026-11-01T00:00:00Z". The cutover has the API ship calendar dates bare, but
 * this shape predates it and still denotes a day, not a moment.
 */
const UTC_MIDNIGHT_RE = /^(\d{4}-\d{2}-\d{2})T00:00:00(\.0+)?Z$/;

/**
 * The calendar day an API value denotes, as "YYYY-MM-DD", from the customer's
 * point of view. Empty string when the value cannot be read as a date.
 *
 * Three shapes arrive from the API and only the last one depends on who is
 * looking:
 *
 * - A bare day ("2026-11-01") and a UTC-midnight timestamp both already name a
 *   day. They are returned as written — reading them as instants would move
 *   them to the previous day anywhere west of UTC, which is the
 *   client-reported bug the tests cover.
 * - An instant ("2026-10-16T23:59:59-04:00") is a moment, and the day it falls
 *   on is the customer's day. Taking its leading characters instead would
 *   report the API's frame as if it were theirs: after the UTC cutover a
 *   promotion ending 23:59:59 Eastern serialises as 2026-10-17T03:59:59Z, and
 *   slicing would name the 17th (CORE-1931).
 *
 * Single source of truth on purpose: the deadline the portal DISPLAYS and the
 * deadline it COMPARES a payment plan against have to be the same day, or it
 * can tell a customer a plan misses a deadline it is in fact inside.
 */
export const calendarDayOf = (value: string | undefined): string => {
  if (!value) return "";

  if (DATE_ONLY_RE.test(value)) return value;

  const utcMidnight = value.match(UTC_MIDNIGHT_RE);
  if (utcMidnight) return utcMidnight[1];

  const dateObj = new Date(value);
  if (isNaN(dateObj.getTime())) return "";

  // Local parts, not toISOString(): that would take the day back to UTC.
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Renders a date the customer reads as a day. The day itself is resolved by
 * calendarDayOf; this only formats it, pinned to UTC so the formatter cannot
 * shift it a second time.
 */
export const formatDate = (
  date: string | undefined,
  locale: string = "en",
): string => {
  const day = calendarDayOf(date);

  if (!day) return "";

  const [year, month, dayOfMonth] = day.split("-").map(Number);

  return new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, dayOfMonth)));
};

/**
 * An instant rather than a calendar day — a clause acceptance, say — rendered
 * in the viewer's own timezone with the clock time. The portal is
 * customer-facing, so the customer's zone is the right frame here (unlike
 * follohq, which renders in the account's).
 *
 * The value must carry its frame: `new Date()` honours a `Z` or an offset when
 * one is present and falls back to the browser's zone when it is not, so a
 * frameless value is read as local time and lands wrong by that offset.
 *
 * That is an API contract bug, not something to patch here. Assuming UTC
 * client-side would be correct only AFTER the cutover and wrong before it, and
 * would then break again once the API starts labelling the value — two fixes
 * that have to land together instead of one that stands alone. See CORE-1931.
 */
export const formatDateTime = (
  value: string,
  locale: string = "en",
): string => {
  const dateObj = new Date(value);

  if (Number.isNaN(dateObj.getTime())) return value;

  return dateObj.toLocaleString(locale, {
    dateStyle: "long",
    timeStyle: "short",
  });
};
